/**
 * @file main.jsx
 * @location src/main.jsx
 * @role 앱 진입점 (Entry Point)
 *
 * 렌더 트리:
 *   StrictMode
 *   └── ErrorBoundary           ← 전역 에러 캐치 (Context 초기화 실패도 잡음)
 *       └── AppProvider         ← 전역 상태 Context 주입
 *           └── App
 *
 * PWA: vite-plugin-pwa 가 dist/sw.js 자동 생성. virtual:pwa-register 가
 *      registerSW() 헬퍼를 노출 — autoUpdate 모드로 신규 SW 즉시 활성.
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { AppProvider } from './store/AppContext';
import { SupabaseGate } from './integrations/supabase/SupabaseGate';
import { ErrorBoundary } from './components/ErrorBoundary';
import { reportWebVitals } from './utils/reportWebVitals';
import { debug } from './utils/debug';
import { initSentry } from './integrations/sentry.js';
import { initGa4 } from './integrations/ga4.js';
import App from './App';
import './styles/index.css';

initSentry();   // SDK init — DSN 없으면 noop

// GA4 — onjeom.com(www 포함)에서만 활성. 로컬/프리뷰 빌드는 데이터 오염 방지로 제외.
// 측정 ID 는 공개값이라 코드에 둬도 무방(env 로 덮어쓰기 가능).
const GA4_ID = import.meta.env.VITE_GA4_ID || 'G-YMWT162P0F';
const ga4Host = typeof window !== 'undefined' ? window.location.hostname : '';
if (ga4Host === 'onjeom.com' || ga4Host === 'www.onjeom.com') initGa4(GA4_ID);

// E2E 우회 — VITE_E2E(.env.e2e, `npm run dev:e2e`) 면 SupabaseGate 없이 App 직접 마운트:
// 로컬우선 경로로 부팅해 Playwright 가 localStorage 시드만으로 결정적 테스트.
// 프로덕션 빌드(mode production)는 .env.e2e 미로드 → 항상 게이트 마운트.
const E2E = import.meta.env.VITE_E2E;

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <ErrorBoundary>
      <AppProvider>
        {E2E ? <App /> : (
          <SupabaseGate>
            <App />
          </SupabaseGate>
        )}
      </AppProvider>
    </ErrorBoundary>
  </StrictMode>
);

// PWA service worker 등록 — 빌드 시점에 virtual 모듈 생성됨.
// dev 환경에서는 vite-plugin-pwa 의 devOptions.enabled 가 false 라 noop.
// Core Web Vitals 측정 — 기본은 DEV 콘솔. 백엔드 전송 도입 시 transport 교체.
reportWebVitals();

if ('serviceWorker' in navigator) {
  import('virtual:pwa-register').then(({ registerSW }) => {
    registerSW({
      immediate: true,
      onRegisterError(err) {
        console.error('[pwa] SW 등록 실패:', err);
      },
    });
  }).catch(err => {
    // dev 모드에서 virtual 모듈 미존재 — 정상
    debug('[pwa] virtual:pwa-register 없음 (dev 환경)', err?.message);
  });
}
